import { FeatureRow } from '../components/FeatureShowcase';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Brain, Sparkles, Rocket, Lightbulb, Check } from 'lucide-react';
import { companyInfo, heroStats, crmFeaturesCopy, subscriptionPlans, enterprisePlan } from '../data/mockData';
import {
  FeatureIcon1,
  FeatureIcon2,
  FeatureIcon3,
  FeatureIcon4,
  FeatureIcon5,
  FeatureIcon6,
  FeatureIcon7,
  FeatureIcon8,
  FeatureIcon9
} from '../components/Icons/Icons'

const FEATURE_SHOTS = [
  [
    { src: '/images/homeShot1.webp', title: 'Account intel', alt: 'AI account research with SWOT highlights, red flags and a strategic engagement report' },
    { src: '/images/homeShot7.webp', title: 'Find leads', alt: 'Find Leads form with target products, services and departments and a list of matching contacts' },
  ],
  [
    { src: '/images/homeContactOverview.webp', title: 'Contact overview', alt: 'Genie giving an overview of a contact and recent interactions' },
    { src: '/images/homeDealOrchestration.webp', title: 'Deal orchestration', alt: 'Genie advising on how to negotiate a deal and which services to cross-sell' },
  ],
  [
    { src: '/images/homeShot5.webp', title: 'Forecast Dashboard', alt: 'Current year forecast chart showing year-to-date performance, forecast and sales targets' },
    { src: '/images/homeShot6.webp', title: 'Live Forecast', alt: 'Forecast table showing year-to-date performance, projected sales and targets by sales team' },
  ],
];


const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      {/* <section className="hero-section">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="display-lg text-white mb-6">
              {companyInfo.tagline}
            </h1>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              {companyInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Request Free Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section> */}
      <section className="hero-section min-h-screen flex items-center">
        <div className="container relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center min-h-screen">
            {/* Left: Text Content - Centered */}
            <div className="flex flex-col justify-center items-center text-center py-14">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight ">
                {companyInfo.tagline}
              </h1>
              <p className="text-lg text-gray-300 mb-8 max-w-xl">
                {companyInfo.description}
              </p>
              <div className="flex gap-4">
                <Link to="/demo">
                  <Button className="btn-primary glow-effect">
                    Request Free Demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            {/* Right: Full Space Image */}
            <div className="relative h-full min-h-[500px] md:min-h-[700px] flex items-center justify-center">
              <img
                src='/images/homeBanner.webp'
                loading="eager"
                fetchpriority="high"
                alt="Hero Illustration"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding">
        <div className="container">
          <div className="stats-grid">
            {heroStats.map((stat, index) => (
              <div key={index} className="stat-item animate-fade-in">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Highlight Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Sparkles className="h-6 w-6 text-blue-400" />
              <span className="text-blue-400 font-medium">Native Intelligence</span>
              <Sparkles className="h-6 w-6 text-blue-400" />
            </div>
            <h2 className="display-md text-white mb-6">
              AI-Native CRM Technology
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Unlike CRMs with AI bolted on as an afterthought, GrowSquare was built AI-native from the ground up. It learns your company, products, and services, then uses that knowledge to discover the right accounts, find and score leads, handle outreach and deal orchestration, and deliver real-time forecasts to guide every decision. Your team spends less time on admin and more time closing.
            </p>
          </div>

          <div className="space-y-16 lg:space-y-10 mb-16">
            {crmFeaturesCopy.slice(0, 3).map((feature, i) => {
              const IconComponent = {
                FeatureIcon1,
                FeatureIcon2,
                FeatureIcon3,
                FeatureIcon4,
                FeatureIcon5,
                FeatureIcon6,
                FeatureIcon7,
                FeatureIcon8,
                FeatureIcon9
              }[feature.icon];
              const shots = FEATURE_SHOTS[i];

              return (
                <FeatureRow key={i} images={shots} reverse={i % 2 === 1}>
                  <div className="w-12 h-12 rounded-lg bg-blue-400/10 flex items-center justify-center mb-5">
                    <IconComponent className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="h3 text-white mb-3">{feature.title}</h3>
                  <p className="body-lg mb-5">{feature.subTitle}</p>
                  <p className="body-md mb-6">{feature.description}</p>
                  <ul className="space-y-2 mb-8">
                    {feature.benefits.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-slate-300">
                        <Check className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/products/${feature.title}`}>
                    <Button className="btn-secondary">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </FeatureRow>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/checkout?plan=Trial&billing=monthly">
              {/* < Link to="/contact"> */}
              <Button className="btn-primary glow-effect">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>

            <Link to="/products">
              <Button className="btn-secondary">
                Explore Features
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Subscription Plans Section */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Rocket className="h-6 w-6 text-blue-400" />
              <span className="text-blue-400 font-medium">Choose Your Plan</span>
            </div>
            <h2 className="display-md text-white mb-6">
              Get Your CRM Working for You Today
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              Pick a plan that fits your team now and scale as you grow. Start with a free trial and see how an AI-native CRM cuts admin and speeds up your sales process.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-6">
            {subscriptionPlans.map((plan, i) => (
              <Card key={i} className={`feature-card ${plan.highlight ? 'border-blue-500 bg-blue-500/5' : ''}`}>
                <CardHeader>
                  <CardTitle className="h2 text-white flex items-center justify-between space-x-2">{plan.name}  {plan.highlight && (
                    <div className="bg-blue-500 text-white newtext-sm font-medium px-3 py-1 rounded-full w-fit">
                      Most Popular
                    </div>
                  )}</CardTitle>
                  <div className="mb-4" style={{ display: "none" }}>
                    <span className="text-4xl font-bold text-blue-400">{plan.price}</span>
                    {plan.duration && <span className="text-slate-400 ml-2">{plan.duration}</span>}
                  </div>
                  <CardDescription className="body-md">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 mb-8">
                    {plan.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <Check className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <span className="body-sm text-slate-300">{highlight}</span>
                      </div>
                    ))}
                    <ul className="space-y-3">
                      <li >
                        <div className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" style={{ visibility: "hidden" }} />
                          <Link to='/plans' className="body-sm text-slate-300 hover:text-green-400">...and many more</Link>
                        </div>
                      </li>
                    </ul>
                  </div>
                  {plan.buttonAction === 'trial' ? (
                    <>
                      <Link to="/checkout?plan=Trial&billing=monthly">
                        {/* < Link to="/contact"> */}
                        <Button className="btn-primary w-full">
                          {plan.buttonText}
                        </Button>
                      </Link></>
                  ) : (
                    //  <Link to={`/checkout?plan=${plan.id}`}>
                    //   <Button className={`w-full ${plan.highlight ? 'btn-primary glow-effect' : 'btn-secondary'}`}>
                    //     {plan.buttonText}
                    //   </Button>
                    // </Link>
                    <Link to="/contact">
                      <Button className="w-full btn-primary glow-effect">
                        {plan.buttonText}
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="feature-card mb-6">
            <CardHeader>
              <CardTitle className="h3 text-white">{enterprisePlan.name}</CardTitle>
              <CardDescription className="body-md">
                {enterprisePlan.subTitle}
              </CardDescription>
              <CardDescription className="body-sm text-slate-400">
                {enterprisePlan.description}
              </CardDescription>
              <CardDescription className="body-sm text-slate-400">
                {enterprisePlan.contactUs}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/contact">
                <Button className="btn-primary">
                  {enterprisePlan.buttonText}
                </Button>
              </Link>
            </CardContent>
            {/* <CardContent>
              <div className="bg-green-400/10 rounded-lg p-4">
                <p className="body-md font-semibold text-green-400">useCase.result</p>
              </div>
            </CardContent> */}
          </Card>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/plans">
              <Button className="btn-primary glow-effect">
                View Plan Features
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Lightbulb className="h-8 w-8 text-blue-400" />
                <h2 className="display-md text-white">About CXSquare Solutions</h2>
              </div>
              <p className="body-lg mb-6">
                CXSquare was founded in 2026 on a simple belief that growing businesses deserve enterprise-grade tools without the complexity or the cost.
              </p>
              <p className="body-lg mb-6">
                Our team of engineers, data scientists, and sales professionals built the platform around AI from day one. It learns your company, products, and services, then helps your team anticipate customer needs and act at the right moment.
              </p>
              <p className="body-lg mb-6">
                We believe great sales start with great relationships, and software should help your team build them. Whether you are building your first pipeline or managing a global operation, CXSquare helps you grow in a way that is predictable and scalable.
              </p>
            </div>

            {/* Right Column */}
            <div className="flex items-center justify-center h-full">
              <img
                src='/images/aboutImg.webp'
                loading="eager"
                fetchpriority="high"
                alt="Centered Image"
                className="w-full h-full max-h-[600px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <Rocket className="h-8 w-8 text-blue-400" />
              <h2 className="display-md text-white">
                See what AI-native actually looks like
              </h2>
            </div>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Book a quick walkthrough and see how GrowSquare automates the busywork and sharpens your decisions. Built for teams that want results, not buzzwords.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  See It in Action
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Talk to Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default HomePage;